/**
 * ============================================================================
 * lsc-media-with-text.js
 * ----------------------------------------------------------------------------
 * LSC - Behaviour for sections/lsc-media-with-text.liquid ("LSC Media with
 * text"). Loaded with `defer` by that section only.
 *
 * Structure
 * ---------
 *   <lsc-media-with-text>   One custom element per section instance. It only
 *                           decides which mode applies (video or parallax)
 *                           and registers itself with the matching shared
 *                           service below. Every listener it adds is tied to
 *                           an AbortController, so disconnecting the element
 *                           (theme editor re-render, section reorder) removes
 *                           all of them, and reconnecting starts clean.
 *
 *   parallax (shared)       ONE IntersectionObserver, ONE scroll + resize
 *                           listener and ONE requestAnimationFrame loop for
 *                           every parallax instance on the page. Each frame
 *                           first measures all on-screen instances, then
 *                           writes all of them (no read/write interleaving,
 *                           so no forced re-layouts between instances). The
 *                           scroll listener exists only while at least one
 *                           instance is on screen.
 *
 *   video (shared)          Two shared IntersectionObservers with different
 *                           jobs: one loads the source 300px before the video
 *                           arrives, the other plays / pauses autoplaying
 *                           videos as they actually enter / leave the screen.
 *
 * Single sources of truth
 * -----------------------
 *   - The desktop/mobile breakpoint and the parallax zoom live in the
 *     section CSS. This file never hard-codes either: it reads the active
 *     intensity from the computed `--lsc-mwt-intensity` custom property
 *     (re-read on resize, so crossing the breakpoint switches live).
 *   - User-facing strings live in Liquid (data-label-* attributes).
 *
 * Accessibility
 * -------------
 *   `prefers-reduced-motion: reduce` disables parallax (image sits still at
 *   1:1) and cancels autoplay (native controls are shown instead).
 *   Autoplaying video has a pause/play button (WCAG 2.2.2); once the shopper
 *   pauses, scrolling never resumes it.
 * ============================================================================
 */
(function () {
  'use strict';

  // The section can appear more than once per page and each instance
  // includes this script tag. The browser runs each copy, so only the first
  // one defines anything.
  if (customElements.get('lsc-media-with-text')) return;

  // Live OS preference, shared by every instance.
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ==========================================================================
   * PARALLAX SERVICE (shared by all instances)
   * ======================================================================== */
  var parallax = (function () {
    var visible = new Set();   // instances currently near the viewport
    var queued = false;        // a rAF frame is already scheduled
    var listening = false;     // scroll/resize listeners are attached
    var remeasure = true;      // intensities must be re-read from CSS

    // One observer for every parallax frame on the page. 100px margin so the
    // position is already correct when the frame scrolls into view.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target.__lscHost;
        if (!el) return;
        if (entry.isIntersecting) {
          visible.add(el);
          // Re-read the intensity from CSS on entry. The resize listener is
          // only attached while something is visible, so a resize that
          // crossed the breakpoint while this instance was off-screen would
          // otherwise leave it using the old breakpoint's intensity (more
          // travel than the new zoom covers, exposing the frame's edges).
          el.intensity = null;
          // Keep a GPU layer only while the frame is near the screen.
          el.parallaxTarget.classList.add('is-parallax-live');
        } else {
          visible.delete(el);
          el.parallaxTarget.classList.remove('is-parallax-live');
        }
      });
      syncListeners();
      schedule();
    }, { rootMargin: '100px 0px' });

    // Attach the window listeners only while something is visible, so a page
    // scrolled far away from every instance does no work at all.
    function syncListeners() {
      if (visible.size && !listening) {
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });
        listening = true;
      } else if (!visible.size && listening) {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', onResize);
        listening = false;
      }
    }

    function onResize() {
      // The viewport may have crossed the CSS breakpoint, which changes the
      // active intensity. Re-read it on the next frame.
      remeasure = true;
      schedule();
    }

    // Throttle to one update per animation frame for all instances together.
    function schedule() {
      if (queued || !visible.size) return;
      queued = true;
      window.requestAnimationFrame(frame);
    }

    function frame() {
      queued = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var items = [];

      // READ phase: geometry (and, after a resize, the active intensity) for
      // every visible instance, before anything is written.
      visible.forEach(function (el) {
        if (remeasure || el.intensity == null) {
          var raw = parseFloat(getComputedStyle(el.parallaxTarget).getPropertyValue('--lsc-mwt-intensity'));
          // Clamp to the settings' range; the CSS zoom covers up to 30.
          el.intensity = Math.min(Math.max(isNaN(raw) ? 0 : raw, 0), 30);
        }
        items.push({ el: el, rect: el.frame.getBoundingClientRect() });
      });
      remeasure = false;

      // WRITE phase.
      items.forEach(function (item) {
        var rect = item.rect;
        // progress: 0 when the frame's top edge touches the bottom of the
        // viewport, 1 when its bottom edge leaves the top. Clamped to 0..1.
        var progress = (vh - rect.top) / (vh + rect.height);
        progress = Math.min(Math.max(progress, 0), 1);
        // Map 0..1 to -intensity..+intensity: the image drifts down as the
        // page scrolls, so it appears to move slower than the page.
        var offset = (progress - 0.5) * 2 * item.el.intensity;
        item.el.parallaxTarget.style.setProperty('--lsc-mwt-shift', offset.toFixed(3) + '%');
      });
    }

    return {
      add: function (el) {
        el.frame.__lscHost = el;
        el.intensity = null; // read from CSS on its first frame
        observer.observe(el.frame);
      },
      remove: function (el) {
        observer.unobserve(el.frame);
        visible.delete(el);
        delete el.frame.__lscHost;
        syncListeners();
      }
    };
  })();

  /* ==========================================================================
   * VIDEO SERVICE (shared by all instances)
   * ======================================================================== */
  var videos = (function () {
    // Job 1: attach the source shortly before the video scrolls into view.
    // Fires once per video, then stops watching it.
    var loader = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target.__lscHost;
        loader.unobserve(entry.target);
        if (el) el.attachSource();
      });
    }, { rootMargin: '300px 0px' });

    // Job 2: play / pause autoplaying videos as they enter / leave the
    // screen. No margin, so nothing plays while still off-screen. A quarter
    // visible counts as "on screen".
    var player = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target.__lscHost;
        if (el) el.setOnScreen(entry.isIntersecting);
      });
    }, { threshold: 0.25 });

    return {
      add: function (el, autoplay) {
        el.video.__lscHost = el;
        if (!el.video.dataset.loaded) loader.observe(el.video);
        if (autoplay) player.observe(el.video);
      },
      remove: function (el) {
        loader.unobserve(el.video);
        player.unobserve(el.video);
        delete el.video.__lscHost;
      }
    };
  })();

  /* ==========================================================================
   * CUSTOM ELEMENT
   * ======================================================================== */
  class LscMediaWithText extends HTMLElement {
    connectedCallback() {
      // Fresh controller per connection; aborting it removes every listener
      // added with { signal } below in one go.
      this.abort = new AbortController();

      this.video = this.querySelector('video[data-lsc-video]');
      this.parallaxTarget = this.querySelector('[data-lsc-parallax-target]');
      this.frame = this.querySelector('.lsc-mwt__frame');

      if (this.video) {
        this.initVideo();
      } else if (this.hasAttribute('data-parallax') && this.parallaxTarget && this.frame) {
        this.initParallax();
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.mode === 'parallax') parallax.remove(this);
      if (this.mode === 'video') videos.remove(this);
      this.mode = null;
    }

    /* ------------------------------------------------------------------------
     * Parallax
     * ---------------------------------------------------------------------- */
    initParallax() {
      // Reduced motion: never move or zoom the image.
      if (reducedMotion.matches) return;

      this.mode = 'parallax';
      // Switches on the transform (see "Parallax layer" in the section CSS).
      // The shift is measured on the frame, not this host element: with
      // "Limit media height to text" the host fills the whole column while
      // the frame inside can be shorter and centred.
      this.parallaxTarget.classList.add('is-parallax-active');
      parallax.add(this);
    }

    /* ------------------------------------------------------------------------
     * Video
     * ---------------------------------------------------------------------- */
    initVideo() {
      var video = this.video;
      var signal = this.abort.signal;

      this.mode = 'video';
      this.autoplay = video.hasAttribute('data-autoplay');
      this.toggle = this.querySelector('[data-lsc-video-toggle]');

      // Kept across reconnects (stored on the <video>, which survives a
      // section reorder) so a shopper's pause is never undone by a re-render.
      this.userPaused = video.dataset.userPaused === 'true';

      // Reduced motion: no autoplay. Native controls instead of the custom
      // toggle (they already include play/pause).
      if (this.autoplay && reducedMotion.matches) {
        this.autoplay = false;
        video.controls = true;
        if (this.toggle) this.toggle.hidden = true;
      }

      if (this.toggle && !this.toggle.hidden) {
        var toggle = this.toggle;
        var labelPlay = toggle.getAttribute('data-label-play') || '';
        var labelPause = toggle.getAttribute('data-label-pause') || '';

        // Keep icon and label in step with the real playback state, whatever
        // changed it (button, scrolling away, browser blocking playback).
        var sync = function () {
          toggle.classList.toggle('is-paused', video.paused);
          toggle.setAttribute('aria-label', video.paused ? labelPlay : labelPause);
        };
        video.addEventListener('play', sync, { signal: signal });
        video.addEventListener('pause', sync, { signal: signal });
        sync();

        toggle.addEventListener('click', function () {
          if (video.paused) {
            this.setUserPaused(false);
            this.attachSource();
            this.safePlay();
          } else {
            this.setUserPaused(true);
            video.pause();
          }
        }.bind(this), { signal: signal });
      }

      videos.add(this, this.autoplay);
    }

    setUserPaused(value) {
      this.userPaused = value;
      this.video.dataset.userPaused = value ? 'true' : 'false';
    }

    // Called by the shared "player" observer for autoplaying videos only.
    setOnScreen(onScreen) {
      this.onScreen = onScreen;
      if (!this.autoplay) return;
      if (onScreen && !this.userPaused) {
        this.attachSource(); // normally already done by the loader
        this.safePlay();
      } else if (!onScreen) {
        this.video.pause();
      }
    }

    // Idempotent: builds the <source> from the data attributes Liquid wrote,
    // once. Does not start playback; that is the player observer's job.
    attachSource() {
      var video = this.video;
      if (video.dataset.loaded) return;
      var src = video.getAttribute('data-src');
      if (!src) return;

      var source = document.createElement('source');
      source.src = src;
      var type = video.getAttribute('data-type');
      if (type) source.type = type;
      video.appendChild(source);

      video.dataset.loaded = 'true';
      video.load(); // tell the element its sources changed
    }

    safePlay() {
      // play() rejects if the browser blocks playback (e.g. iOS Low Power
      // Mode). Show native controls then, so a tap can still start it.
      var video = this.video;
      if (this.autoplay) video.muted = true; // unprompted playback must be muted
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.controls = true;
        });
      }
    }
  }

  customElements.define('lsc-media-with-text', LscMediaWithText);
})();
